import React, { useState, useMemo } from 'react';
import { Table, Button, Tag, Space, Input, Select, Card, Row, Col, Typography, Tooltip, message, Empty, Spin, Alert, Badge } from 'antd';
import { EditOutlined, SearchOutlined, FileTextOutlined, CheckCircleOutlined, AppstoreOutlined, TableOutlined } from '@ant-design/icons';
import { useGetQuestionTemplatesQuery, useGetBrandsQuery } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { useIntl } from 'react-intl';
import { IQuestionTemplate } from '../../services/nestjs-base-api';
import { UserRoleEnum } from '../../enums/UserRoleEnum';
import { TemplateTypeEnum } from '../../enums/TemplateTypeEnum';
import QuestionTemplateEditModal from './QuestionTemplateEditModal';
import CategoryOverview from './CategoryOverview';
import { getColorForCategory } from './questionTemplateColors';
import { getDisplayName } from '../../utils/elementDisplayNames';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

const QuestionTemplateList: React.FC = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const [selectedTemplate, setSelectedTemplate] = useState<IQuestionTemplate | null>(null);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [coreFilter, setCoreFilter] = useState<'all' | 'core' | 'non-core'>('all');
    const [searchText, setSearchText] = useState('');
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

    // Get the organization's brands to determine the primary brand's template type
    const { data: brands } = useGetBrandsQuery({
        jwtToken: user?.jwtToken || '',
        organisationId: user?.organisation?.id || 0
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Determine the template type to use (primary brand's type or default to durable)
    const templateTypeToUse = useMemo(() => {
        if (!brands || brands.length === 0) return TemplateTypeEnum.DURABLE;
        
        // Use the first brand as the primary brand
        const primaryBrand = brands[0];
        return primaryBrand.templateType || TemplateTypeEnum.DURABLE;
    }, [brands]);

    const { data, isLoading, isError, error, refetch } = useGetQuestionTemplatesQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !user?.jwtToken }
    );

    // Handle both array and object response structures and filter by template type
    const templates = useMemo(() => {
        const allTemplates = Array.isArray(data) ? data : (data?.data || []);
        
        // Filter templates by the organization's template type
        return allTemplates.filter(template => 
            template.templateType === templateTypeToUse
        );
    }, [data, templateTypeToUse]);

    const filteredTemplates = useMemo(() => {
        return templates.filter(template => {
            const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
            const matchesCore = coreFilter === 'all' || 
                (coreFilter === 'core' && template.isCore) || 
                (coreFilter === 'non-core' && !template.isCore);
            const matchesSearch = searchText === '' || 
                template.text.toLowerCase().includes(searchText.toLowerCase()) ||
                template.code.toLowerCase().includes(searchText.toLowerCase());
            
            return matchesCategory && matchesCore && matchesSearch;
        });
    }, [templates, categoryFilter, coreFilter, searchText]);

    const categories = useMemo(() => {
        const cats = new Set(templates.map(t => t.category));
        return Array.from(cats).sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
    }, [templates]);

    // Parse roles if it's a JSON string
    const userRoles = useMemo(() => {
        if (!user?.roles) return [];
        if (Array.isArray(user.roles)) return user.roles;
        if (typeof user.roles === 'string') {
            try {
                return JSON.parse(user.roles);
            } catch (e) {
                console.error('Failed to parse user roles:', e);
                return [];
            }
        }
        return [];
    }, [user?.roles]);

    // Check if user can edit - only SuperAdmin and Admin roles
    const canEdit = userRoles.includes(UserRoleEnum.SuperAdmin) || 
                    userRoles.includes(UserRoleEnum.Admin);

    const handleEdit = (template: IQuestionTemplate) => {
        setSelectedTemplate(template);
        setIsEditModalVisible(true);
    };

    const handleEditModalClose = () => {
        setIsEditModalVisible(false);
        setSelectedTemplate(null);
    };

    const handleEditSuccess = () => {
        message.success(intl.formatMessage({ id: 'surveyTemplates.success.updated' }));
        refetch();
        handleEditModalClose();
    };

    const renderQuestionText = (text: string) => {
        const parts = text.split(/(\{\{brand}})/);
        return (
            <span>
                {parts.map((part, index) => 
                    part === '{{brand}}' ? (
                        <Tag key={index} color="blue" style={{ margin: '0 4px' }}>
                            {'{{brand}}'}
                        </Tag>
                    ) : (
                        <span key={index}>{part}</span>
                    )
                )}
            </span>
        );
    };

    const columns: any[] = useMemo(() => [
        {
            title: intl.formatMessage({ id: 'surveyTemplates.table.code' }),
            dataIndex: 'code',
            key: 'code',
            width: 80,
            sorter: (a: IQuestionTemplate, b: IQuestionTemplate) => a.code.localeCompare(b.code),
        },
        {
            title: intl.formatMessage({ id: 'surveyTemplates.table.questionText' }),
            dataIndex: 'text',
            key: 'text',
            render: (text: string, record: IQuestionTemplate) => (
                <div>
                    {renderQuestionText(text)}
                </div>
            ),
        },
        {
            title: intl.formatMessage({ id: 'surveyTemplates.table.category' }),
            dataIndex: 'category',
            key: 'category',
            width: 250,
            filters: categories.map(cat => ({ text: getDisplayName(cat), value: cat })),
            onFilter: (value: any, record: IQuestionTemplate) => record.category === value,
            render: (category: string) => (
                <Tag color={getColorForCategory(category)}>{getDisplayName(category)}</Tag>
            ),
        },
        // Only add Actions column for users who can edit
        ...(canEdit ? [{
            title: intl.formatMessage({ id: 'surveyTemplates.table.actions' }),
            key: 'actions',
            width: 80,
            align: 'center' as const,
            render: (_: any, record: IQuestionTemplate) => (
                <Space>
                    <Tooltip title={intl.formatMessage({ id: 'surveyTemplates.table.edit' })}>
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        }] : [])
    ], [intl, categories, canEdit]);

    if (isLoading) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spin size="large" tip={intl.formatMessage({ id: 'surveyTemplates.loading' })} />
            </div>
        );
    }

    if (isError) {
        return (
            <div style={{ padding: '24px' }}>
                <Alert
                    message={intl.formatMessage({ id: 'surveyTemplates.error.title' })}
                    description={error?.toString() || intl.formatMessage({ id: 'surveyTemplates.error.description' })}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={() => refetch()}>
                            {intl.formatMessage({ id: 'surveyTemplates.error.retry' })}
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={2} style={{ margin: 0 }}>
                    {intl.formatMessage({ id: 'surveyTemplates.title' })}
                </Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Text type="secondary">Product Type:</Text>
                    <Tag color={templateTypeToUse === TemplateTypeEnum.DURABLE ? 'blue' : 'green'} style={{ fontSize: '12px' }}>
                        {templateTypeToUse === TemplateTypeEnum.DURABLE ? 'Durable goods' : 'Perishable goods'}
                    </Tag>
                </div>
            </div>

            {/* Question Templates Introduction */}
            <Card style={{ marginBottom: 24, backgroundColor: '#f8fafc', borderColor: '#e1e7ef' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                    <div style={{ 
                        backgroundColor: '#4338ca', 
                        borderRadius: '50%', 
                        padding: 12, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        minWidth: 48,
                        height: 48
                    }}>
                        <FileTextOutlined style={{ color: 'white', fontSize: '20px' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Title level={4} style={{ margin: 0, marginBottom: 12, color: '#1f2937' }}>
                            {intl.formatMessage({ id: 'surveyTemplates.introduction.title' })}
                        </Title>
                        <div style={{ marginBottom: 16 }}>
                            <Paragraph style={{ margin: 0, marginBottom: 12, color: '#4b5563', fontSize: '15px' }}>
                                {intl.formatMessage({ id: 'surveyTemplates.introduction.chunking' })}
                            </Paragraph>
                            <Paragraph style={{ margin: 0, marginBottom: 12, color: '#4b5563', fontSize: '15px' }}>
                                {intl.formatMessage({ id: 'surveyTemplates.introduction.research' })}
                            </Paragraph>
                            <Paragraph style={{ margin: 0, color: '#4b5563', fontSize: '15px' }}>
                                {intl.formatMessage({ id: 'surveyTemplates.introduction.interaction' })}
                            </Paragraph>
                        </div>
                        {templates.length > 0 && (
                            <div style={{ 
                                backgroundColor: 'white', 
                                padding: 16, 
                                borderRadius: 8, 
                                border: '1px solid #e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12
                            }}>
                                <CheckCircleOutlined style={{ color: '#10b981', fontSize: '18px' }} />
                                <Text style={{ color: '#374151', fontWeight: 500 }}>
                                    {intl.formatMessage(
                                        { id: 'surveyTemplates.introduction.totalQuestions' },
                                        { count: templates.length }
                                    )}
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
            </Card>
            
            {templates.length === 0 ? (
                <Empty
                    description={intl.formatMessage({ id: 'surveyTemplates.empty' })}
                    style={{ marginTop: 48 }}
                />
            ) : (
                <>
                    {/* Controls Bar */}
                    <Card style={{ marginBottom: 24 }}>
                        <Row gutter={16} style={{ marginBottom: 0 }}>
                    <Col span={6}>
                        <Input
                            placeholder={intl.formatMessage({ id: 'surveyTemplates.search.placeholder' })}
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Col>
                    <Col span={5}>
                        <Select
                            placeholder={intl.formatMessage({ id: 'surveyTemplates.filter.category' })}
                            value={categoryFilter}
                            onChange={setCategoryFilter}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">{intl.formatMessage({ id: 'surveyTemplates.filter.allCategories' })}</Option>
                            {categories.map((cat, index) => (
                                <Option key={cat} value={cat}>
                                    <Badge color={getColorForCategory(cat, index)} text={getDisplayName(cat)} />
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={5}>
                        <Select
                            placeholder={intl.formatMessage({ id: 'surveyTemplates.filter.type' })}
                            value={coreFilter}
                            onChange={setCoreFilter}
                            style={{ width: '100%' }}
                        >
                            <Option value="all">{intl.formatMessage({ id: 'surveyTemplates.filter.allQuestions' })}</Option>
                            <Option value="core">{intl.formatMessage({ id: 'surveyTemplates.filter.coreOnly' })}</Option>
                            <Option value="non-core">{intl.formatMessage({ id: 'surveyTemplates.filter.nonCoreOnly' })}</Option>
                        </Select>
                    </Col>
                    <Col span={8} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button.Group>
                            <Button
                                type={viewMode === 'cards' ? 'primary' : 'default'}
                                icon={<AppstoreOutlined />}
                                onClick={() => setViewMode('cards')}
                            >
                                Cards View
                            </Button>
                            <Button
                                type={viewMode === 'table' ? 'primary' : 'default'}
                                icon={<TableOutlined />}
                                onClick={() => setViewMode('table')}
                            >
                                Table View
                            </Button>
                        </Button.Group>
                        </Col>
                        </Row>
                    </Card>

                    {/* Conditional Content Rendering */}
                    {viewMode === 'cards' ? (
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col span={24}>
                                <CategoryOverview templates={filteredTemplates} />
                            </Col>
                        </Row>
                    ) : (
                        <Card>
                            <Table
                                columns={columns}
                                dataSource={filteredTemplates}
                                rowKey={(record) => record.id || record.code}
                                loading={isLoading}
                                pagination={{
                                    pageSize: 20,
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} templates`,
                                }}
                                size="middle"
                                scroll={{ y: 'calc(100vh - 400px)' }}
                            />
                        </Card>
                    )}
                </>
            )}

            {selectedTemplate && (
                <QuestionTemplateEditModal
                    visible={isEditModalVisible}
                    template={selectedTemplate}
                    onClose={handleEditModalClose}
                    onSuccess={handleEditSuccess}
                />
            )}
        </div>
    );
};

export default QuestionTemplateList;